// Moved from `src/features/voice-assistant/client/voiceSocket.ts` with its
// subject. Both app reads it carried cross the `configureAgentsData` seam and
// are resolved inside `buildUrl()`, per connect attempt, exactly where
// `getAuthTokenForHttpClientSync()` and `getDomains()` were called before — so
// a token that changes between the first attempt and a reconnect is still
// re-read, which is what the backoff loop relies on.
import { appendQueryParam } from '@heidi/api/config';
import { logger } from '@heidi/runtime/logger';

import type { ClientMessage, ServerMessage } from '../../contracts/voice-protocol';
import { getAgentsDataConfig } from '../config';

const VOICE_WS_PATH = '/api/v2/ml-scribe/voice/realtime';
const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 8000;
// After a deliberate `end`, the server finalizes the session and answers with
// `chat_exported` (the Work chat seeded from the call) before it closes the
// socket. Closing our side immediately would drop that frame, so linger until
// the server hangs up. The finalize can take a few seconds (project create +
// transcript seeding; degraded evidence-config builds add a bounded ~3s), so
// the cap is generous — it only fires if the server never closes, and the
// socket is invisible to the user by this point.
const END_LINGER_MS = 15000;

/**
 * Terminal socket error, as data rather than display copy: the socket is
 * headless (`/data`), so the two local failures cross as codes the UI
 * localizes, and a server-provided close reason crosses verbatim as text.
 */
export type VoiceSocketError =
  | { kind: 'not_signed_in' }
  | { kind: 'voice_unavailable' }
  | { kind: 'server'; text: string };

export interface VoiceSocketHandlers {
  onOpen: () => void;
  onControl: (message: ServerMessage) => void;
  onAudio: (pcm: ArrayBuffer) => void;
  onClose: (intentional: boolean) => void;
  onError: (error: VoiceSocketError) => void;
}

/**
 * Reconnecting WebSocket for the voice session. Carries binary PCM audio and
 * JSON control frames. Backoff/retry pattern follows
 * `models/scribeRecordModel.ts`'s ReconnectingWebSocket.
 */
export class VoiceSocket {
  private socket: WebSocket | null = null;
  private readonly handlers: VoiceSocketHandlers;
  private attempts = 0;
  private closedByClient = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private lingerTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(handlers: VoiceSocketHandlers) {
    this.handlers = handlers;
  }

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  connect(): void {
    this.closedByClient = false;
    this.open();
  }

  private buildUrl(): string | null {
    const { getAuthTokenSync, resolveWebsocketOrigin, getWorkspaceId } =
      getAgentsDataConfig();
    const token = getAuthTokenSync();
    if (!token) {
      this.handlers.onError({ kind: 'not_signed_in' });
      return null;
    }
    const wsDomain = resolveWebsocketOrigin();
    const url = `${wsDomain}${VOICE_WS_PATH}?token=${encodeURIComponent(token)}`;
    // Read per connect attempt so a backoff retry pins the workspace the user
    // is in NOW: this socket carries no session or resume id, so every
    // reconnect opens a fresh backend session. `null` appends nothing, which
    // the backend reads as "infer the user's default workspace".
    return appendQueryParam(url, 'workspace_id', getWorkspaceId());
  }

  private open(): void {
    const url = this.buildUrl();
    if (!url) return;

    const socket = new WebSocket(url);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    socket.onopen = () => {
      this.attempts = 0;
      this.handlers.onOpen();
    };

    socket.onmessage = (event: MessageEvent) => {
      if (typeof event.data === 'string') {
        try {
          this.handlers.onControl(JSON.parse(event.data) as ServerMessage);
        } catch {
          logger.debug('[voice] dropped non-JSON control frame');
        }
        return;
      }
      if (event.data instanceof ArrayBuffer) {
        this.handlers.onAudio(event.data);
      }
    };

    socket.onerror = (event) => {
      logger.error('[voice] websocket error', event);
    };

    socket.onclose = (event) => {
      this.socket = null;
      if (this.closedByClient) {
        this.handlers.onClose(true);
        return;
      }
      // 1008 = policy violation (unauthorized / disabled / capped): do not retry.
      if (event.code === 1008 || this.attempts >= MAX_RECONNECT_ATTEMPTS) {
        this.handlers.onClose(false);
        if (event.code === 1008) {
          this.handlers.onError(
            event.reason
              ? { kind: 'server', text: event.reason }
              : { kind: 'voice_unavailable' }
          );
        }
        return;
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    this.attempts += 1;
    const delay = Math.min(BASE_BACKOFF_MS * 2 ** (this.attempts - 1), MAX_BACKOFF_MS);
    logger.debug('[voice] reconnecting', { attempt: this.attempts, delay });
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  sendAudio(pcm: ArrayBuffer): void {
    if (this.isOpen) {
      this.socket?.send(pcm);
    }
  }

  sendControl(message: ClientMessage): void {
    if (this.isOpen) {
      this.socket?.send(JSON.stringify(message));
    }
  }

  close(): void {
    this.closedByClient = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // Already draining a previous close() — don't send `end` twice or stack
    // another force-close timer.
    if (this.lingerTimer) return;
    const socket = this.socket;
    if (!socket) return;
    try {
      if (this.isOpen) {
        socket.send(JSON.stringify({ type: 'end' } satisfies ClientMessage));
        // Drain mode: the linger exists only so the `chat_exported` announce
        // can land. The handlers stay wired to the (singleton) controller, so
        // if voice restarts within the window every other late frame from
        // this orphaned socket — audio, `closing`, `error` — would act on the
        // *new* session. Forward the one frame that matters (it is
        // payload-addressed and session-guarded downstream) and drop the
        // rest; hang-up teardown already ran, so `onClose` has nothing left
        // to be told.
        socket.onmessage = (event: MessageEvent) => {
          if (typeof event.data !== 'string') return;
          try {
            const message = JSON.parse(event.data) as ServerMessage;
            if (message.type === 'chat_exported') {
              this.handlers.onControl(message);
              // Announce received — nothing else to wait for.
              socket.close(1000);
            }
          } catch {
            logger.debug('[voice] dropped non-JSON control frame');
          }
        };
        socket.onclose = () => {
          if (this.lingerTimer) {
            clearTimeout(this.lingerTimer);
            this.lingerTimer = null;
          }
          if (this.socket === socket) {
            this.socket = null;
          }
        };
        this.lingerTimer = setTimeout(() => {
          this.lingerTimer = null;
          try {
            socket.close(1000);
          } catch {
            // Server already closed it.
          }
        }, END_LINGER_MS);
        return;
      }
      socket.close(1000);
    } catch (error) {
      logger.debug('[voice] error closing socket', error);
    }
    this.socket = null;
  }
}
